import { pick } from 'underscore';
import { MediaSegment } from 'redux/modules/entities';
import { IEmbedConfig, MediaSegmentInfo } from 'types';

export type MediaSegmentIdData = Pick<MediaSegmentInfo, 'id' | 'revisionId'>;

export type MediaSegmentHitType = 'before' | 'inside' | 'after';

export type FindSegmentResult = {
  position: MediaSegmentHitType;
  segment: MediaSegment;
};

function findSegmentByMillis(
  millis: number,
  segmentData: MediaSegment[],
): FindSegmentResult | undefined {
  if (!segmentData || segmentData.length === 0) {
    return undefined;
  }

  const firstSegment = segmentData[0];
  const lastSegment = segmentData[segmentData.length - 1];

  // before first segment
  if (millis <= firstSegment.startMillis) {
    return {
      position: 'before',
      segment: firstSegment,
    };
  }

  for (let i = 0; i < segmentData.length; i += 1) {
    const segment = segmentData[i];
    const nextSegment = segmentData[i + 1];

    // inside this segment --> normal adjustment
    if (millis <= segment.endMillis) {
      return {
        position: 'inside',
        segment,
      };
    }

    if (nextSegment && millis < nextSegment.startMillis) {
      // gap between segment and nextSegment --> clamp to nextSegment's start
      return {
        position: 'before',
        segment: nextSegment,
      };
    }
  }

  // beyond the last segment
  return {
    position: 'after',
    segment: lastSegment,
  };
}

function findSegmentByAdjustedMillis(
  adjustedMillis: number,
  segmentData: MediaSegment[],
): FindSegmentResult | undefined {
  if (!segmentData || segmentData.length === 0) {
    return undefined;
  }

  const firstSegment = segmentData[0];
  const lastSegment = segmentData[segmentData.length - 1];

  // before first segment
  if (adjustedMillis <= firstSegment.adjustedStartMillis) {
    return {
      position: 'before',
      segment: firstSegment,
    };
  }

  for (let i = 0; i < segmentData.length; i += 1) {
    const segment = segmentData[i];
    const nextSegment = segmentData[i + 1];

    // inside this segment --> normal adjustment
    if (adjustedMillis <= segment.adjustedEndMillis) {
      return {
        position: 'inside',
        segment,
      };
    }

    // gap between segment and nextSegment --> clamp to nextSegment's start
    if (nextSegment && adjustedMillis < nextSegment.adjustedStartMillis) {
      return {
        position: 'before',
        segment: nextSegment,
      };
    }
  }

  // behind the last segment
  return {
    position: 'after',
    segment: lastSegment,
  };
}

export function getMediaSegmentInfoIds(
  config: IEmbedConfig | undefined,
): MediaSegmentIdData[] {
  const { mediaSegmentInfo = [] } = config ?? {};

  return mediaSegmentInfo.map(i => pick(i, ['id', 'revisionId']));
}

export function removeGapsFromTime(
  millis: number,
  segmentData: MediaSegment[],
) {
  const result = findSegmentByMillis(millis, segmentData);

  if (!result) {
    return millis;
  }

  const { position, segment } = result;

  if (position === 'inside') {
    return millis - segment.adjustmentMillis;
  }

  if (position === 'before') {
    return segment.adjustedStartMillis;
  }

  return segment.adjustedEndMillis;
}

export function addGapsToTime(millis: number, segmentData: MediaSegment[]) {
  const result = findSegmentByAdjustedMillis(millis, segmentData);

  if (!result) {
    return millis;
  }

  const { position, segment } = result;

  if (position === 'inside') {
    return millis + segment.adjustmentMillis;
  }

  if (position === 'before') {
    return segment.startMillis;
  }

  return segment.endMillis;
}

export function skipGaps(millis: number, segmentData: MediaSegment[]) {
  if (!segmentData || segmentData.length === 0) {
    return millis;
  }

  // these times include deletions, as does the `milli` parameter
  const clipStartMillis = segmentData[0].startMillis;
  const clipEndMillis = segmentData[segmentData.length - 1].endMillis;

  // if the playback time lies outside of the clip, move playback to the appropriate
  // boundary

  if (millis < clipStartMillis) {
    return clipStartMillis;
  }

  if (millis > clipEndMillis) {
    return clipEndMillis;
  }

  // time lies within the clip, so it has to be adjusted to skip deletions.
  // segments are ordered, so as soon as we find that the playback time lies
  // before the segment start, we know we're in a gap
  for (let i = 0; i < segmentData.length; i += 1) {
    const { startMillis, endMillis } = segmentData[i];

    // if the playback time is within the segment, it's valid
    if (millis >= startMillis && millis <= endMillis) {
      return millis;
    }

    // if the playback time is before a segment start, then it's in a gap and
    // has to be adjusted
    if (millis < startMillis) {
      return startMillis;
    }
  }

  return millis;
}
